import { ChartType } from '@app/src/pages/Assessments/ChartTypeSelector'
import type { CartesianAxisOption } from '@app/src/types/echarts'
import { getMinMaxOnYAxisCountCharts } from '@app/src/utils/statisticsUtils'
import type { SpendClassification } from '@app/src/wf-constants'
import { spendClassificationOrder } from '@app/src/wf-constants'
import { useTheme } from '@mui/material'
import type { EChartsOption } from 'echarts'
import type { XAXisOption, YAXisOption } from 'echarts/types/dist/shared'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import type { StatisticsSummaryPerGroup } from '../DataHubSummaryChart'
import { useSpendConfigsUtils } from './useSpendConfigsUtils'

interface SpendSummaryChartConfigsParams {
  resultsForGroup: StatisticsSummaryPerGroup[]
  selectedChartType: ChartType
}

export enum SummaryChartDataTypes {
  CompaniesAnswered = 'companiesAnswered',
  CompaniesAnsweredDontHaveThis = 'companiesAnsweredDontHaveThis',
  CompaniesNotAnswered = 'companiesNotAnswered',
}

const getMinMaxYAxis = (optionItemResult: StatisticsSummaryPerGroup[]) => {
  const summaryValues = optionItemResult.flatMap(
    res => res.companiesAnswered + res.companiesAnsweredDontHaveThis + res.companiesNotAnswered,
  )
  return getMinMaxOnYAxisCountCharts(summaryValues)
}

export const useSpendSummaryChartConfigs = ({ resultsForGroup, selectedChartType }: SpendSummaryChartConfigsParams) => {
  const { typography } = useTheme()
  const { formatMessage } = useIntl()
  const { getSpendSeriesColor, formatGroupName } = useSpendConfigsUtils()

  const orderedResultsForGroup = resultsForGroup.sort(
    (a, b) => spendClassificationOrder.indexOf(b.groupName) - spendClassificationOrder.indexOf(a.groupName),
  )

  const existingOptions = useMemo(
    () => [
      formatMessage({ id: 'statistics.dataInsights.reported' }),
      formatMessage({ id: 'statistics.dataInsights.dontHaveThis' }),
      formatMessage({ id: 'statistics.dataInsights.noResponse' }),
    ],
    [],
  )

  const { min, max } = getMinMaxYAxis(resultsForGroup)

  const valueAxis: CartesianAxisOption = useMemo(
    () => ({
      type: 'value',
      name: formatMessage({ id: 'statistics.dataInsights.count' }),
      nameLocation: 'middle',
      nameGap: 35,
      min: min,
      max: max,
      minInterval: 1,
      nameTextStyle: {
        fontFamily: typography.fontFamily,
        fontSize: typography.body2.fontSize,
      },
    }),
    [formatMessage, typography.body2.fontSize, typography.fontFamily, min, max],
  )

  const categoryAxis: CartesianAxisOption = useMemo(
    () => ({
      type: 'category',
      data: existingOptions,
      axisLabel: {
        interval: 0,
        fontFamily: typography.fontFamily,
        fontSize: typography.body2.fontSize,
      },
    }),
    [existingOptions, typography.body2.fontSize, typography.fontFamily],
  )

  const horizontalBarChartOptions: EChartsOption = useMemo(() => {
    const xAxis = valueAxis as XAXisOption
    const yAxis = { ...categoryAxis, show: false } as YAXisOption

    return {
      tooltip: {
        axisPointer: {
          type: 'shadow',
        },
      },
      grid: {
        containLabel: false,
        left: 20,
        right: 7,
        top: 30,
        bottom: 90,
      },
      xAxis,
      yAxis,
      legend: {
        icon: 'circle',
        bottom: 10,
        left: 0,
        textStyle: {
          fontFamily: typography.fontFamily,
          fontSize: typography.body2.fontSize,
        },
      },
      series: orderedResultsForGroup.map((r, i) => ({
        name: formatGroupName(r.groupName),
        type: 'bar',
        stack: 'total',
        barCategoryGap: '50%',
        data: [
          {
            name: formatMessage({ id: 'statistics.dataInsights.noResponse' }),
            value: r.companiesNotAnswered,
            groupName: r.groupName,
            summaryType: SummaryChartDataTypes.CompaniesNotAnswered,
            label:
              i === 0
                ? {
                    show: true,
                    position: [0, -14],
                    align: 'left',
                    formatter: '{b}',
                    fontFamily: typography.fontFamily,
                    fontSize: typography.body2.fontSize,
                  }
                : undefined,
          },
          {
            name: formatMessage({ id: 'statistics.dataInsights.dontHaveThis' }),
            value: r.companiesAnsweredDontHaveThis,
            groupName: r.groupName,
            summaryType: SummaryChartDataTypes.CompaniesAnsweredDontHaveThis,
            label:
              i === 0
                ? {
                    show: true,
                    position: [0, -14],
                    align: 'left',
                    formatter: '{b}',
                    fontFamily: typography.fontFamily,
                    fontSize: typography.body2.fontSize,
                  }
                : undefined,
          },
          {
            name: formatMessage({ id: 'statistics.dataInsights.reported' }),
            value: r.companiesAnswered,
            groupName: r.groupName,
            summaryType: SummaryChartDataTypes.CompaniesAnswered,
            label:
              i === 0
                ? {
                    show: true,
                    position: [0, -14],
                    align: 'left',
                    formatter: '{b}',
                    fontFamily: typography.fontFamily,
                    fontSize: typography.body2.fontSize,
                  }
                : undefined,
          },
        ],
        color: getSpendSeriesColor(r.groupName as SpendClassification),
      })),
    }
  }, [valueAxis, categoryAxis, typography.fontFamily, typography.body2.fontSize, formatMessage])

  const verticalBarChartOptions: EChartsOption = useMemo(() => {
    const xAxis = categoryAxis as XAXisOption
    const yAxis = valueAxis as YAXisOption

    return {
      tooltip: {
        axisPointer: {
          type: 'shadow',
        },
      },
      grid: {
        containLabel: true,
        left: 40,
        right: 7,
        top: 30,
        bottom: 50,
      },
      xAxis,
      yAxis,
      legend: {
        icon: 'circle',
        bottom: 10,
        left: 0,
        textStyle: {
          fontFamily: typography.fontFamily,
          fontSize: typography.body2.fontSize,
        },
      },
      series: orderedResultsForGroup.map(r => ({
        name: formatGroupName(r.groupName),
        type: 'bar',
        stack: 'total',
        barCategoryGap: '50%',
        data: [
          {
            name: formatMessage({ id: 'statistics.dataInsights.reported' }),
            value: r.companiesAnswered,
            groupName: r.groupName,
            summaryType: SummaryChartDataTypes.CompaniesAnswered,
          },
          {
            name: formatMessage({ id: 'statistics.dataInsights.dontHaveThis' }),
            value: r.companiesAnsweredDontHaveThis,
            groupName: r.groupName,
            summaryType: SummaryChartDataTypes.CompaniesAnsweredDontHaveThis,
          },
          {
            name: formatMessage({ id: 'statistics.dataInsights.noResponse' }),
            value: r.companiesNotAnswered,
            groupName: r.groupName,
            summaryType: SummaryChartDataTypes.CompaniesNotAnswered,
          },
        ],
        color: getSpendSeriesColor(r.groupName as SpendClassification),
      })),
    }
  }, [categoryAxis, valueAxis, typography.fontFamily, typography.body2.fontSize, formatMessage])

  return selectedChartType === ChartType.Horizontal ? horizontalBarChartOptions : verticalBarChartOptions
}
